// Coercion-compat buckets for value-slot candidates (T7, D6) — encodes the
// plan's normative five-engine table EXACTLY. Direction-sensitive: `lhs` is
// the comparison LHS class, `candidate` the candidate column/routine class.
// Unlisted pairs and anything involving 'unknown'/'null' are neutral and are
// NEVER demoted. Buckets demote, they don't drop (F8).

import type { DatabaseEngineId } from '@/core/engines';
import type { OperandLiteralSqlType } from '@/features/sql-resolver/types/queryStructure';

export type CompatBucket = 'compatible' | 'neutral' | 'lossy' | 'incompatible';

type CoarseClass = 'number' | 'text' | 'boolean' | 'other';

function classOf(t: OperandLiteralSqlType): CoarseClass {
  if (t === 'integer' || t === 'numeric') return 'number';
  if (t === 'text') return 'text';
  if (t === 'boolean') return 'boolean';
  return 'other'; // 'unknown' | 'null'
}

const STRICT_NUM_TEXT: ReadonlySet<DatabaseEngineId> = new Set(['pgsql', 'mssql']);

/** Bucket for comparing an LHS of `lhs` against a candidate of `candidate`
 *  on `engine` (LHS → candidate direction). */
export function compatBucket(
  engine: DatabaseEngineId,
  lhs: OperandLiteralSqlType,
  candidate: OperandLiteralSqlType,
): CompatBucket {
  const l = classOf(lhs);
  const c = classOf(candidate);
  if (l === 'other' || c === 'other') return 'neutral';

  if (l === 'number') {
    if (c === 'number') return 'compatible';
    if (c === 'text') return STRICT_NUM_TEXT.has(engine) ? 'incompatible' : 'lossy';
    return 'neutral'; // boolean
  }
  if (l === 'text') {
    if (c === 'number') return STRICT_NUM_TEXT.has(engine) ? 'incompatible' : 'lossy';
    if (c === 'text') return 'compatible';
    return engine === 'pgsql' ? 'lossy' : 'neutral'; // boolean ('yes' coerces on pg)
  }
  // l === 'boolean'
  if (c === 'number') return 'neutral';
  if (c === 'text') return engine === 'pgsql' ? 'lossy' : 'neutral';
  return 'compatible'; // boolean
}
